import React, { createContext, useState, useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from 'styled-components'

export const BlogContext = createContext({})

export const BlogProvider = BlogContext.Provider
export const BlogConsumer = BlogContext.Consumer

const Blogik = ({
  type = 'blog',
  settings: options,
  filterName = 'categories',
  children,
  defaultSelectedFilters = false,
}) => {
  const settings = {
    ...{
      id: 'blog',
      postIds: [],
      limit: 6,
      usePagination: false,
    },
    ...options,
  }

  const {
    allWordpressPost: { postEdges },
    allWordpressCategory: { edges: categoryEdges },
    // eslint-disable-next-line
  } = useStaticQuery(graphql`
    query BlogikQuery {
      allWordpressPost(sort: { order: DESC, fields: date }) {
        postEdges: edges {
          node {
            ...PostsPreviewFrag
          }
        }
      }
      allWordpressCategory(filter: { wordpress_id: { in: [11, 12, 13, 19] } }) {
        edges {
          node {
            wordpress_id
            name
          }
        }
      }
    }
  `)

  let allPosts = postEdges

  let catEdges = categoryEdges

  const [selectedFilters, setSelectedFilters] = useState(
    defaultSelectedFilters !== false ? defaultSelectedFilters : []
  )
  const [offset, setOffset] = useState(1)

  const setSelectedFiltersLocal = (filter) => {
    /* @ts-ignore */
    const newSelectedFilters = [...selectedFilters]

    const filterIndex = newSelectedFilters.indexOf(filter)

    if (filterIndex === -1) {
      newSelectedFilters.push(filter)
    } else {
      newSelectedFilters.splice(filterIndex, 1)
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        `${settings.id}-filters`,
        JSON.stringify(newSelectedFilters)
      )
    }

    setSelectedFilters(newSelectedFilters)
  }

  const setOffsetLocal = (newOffset) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(`${settings.id}-offset`, newOffset)
    }

    setOffset(newOffset)
  }

  const incrementOffset = () => {
    let newOffset = Number(offset)

    newOffset += 1

    setOffsetLocal(newOffset)
  }

  const decrementOffset = () => {
    let newOffset = Number(offset)

    newOffset -= 1

    setOffsetLocal(newOffset)
  }

  const isSelected = (filter) => selectedFilters.indexOf(filter) !== -1

  const filterPosts = (posts, { ids }) =>
    posts.filter(({ node }, index) => {
      let response = true

      if (
        ids.length === 0 &&
        settings.limit !== null &&
        (index + 1 > offset * settings.limit ||
          (settings.usePagination && offset > index + 1))
      ) {
        return false
      }

      if (ids.length > 0 && ids.indexOf(node.wordpress_id) === -1) {
        return false
      }

      if (selectedFilters.length > 0) {
        response = false

        node.categories.forEach((category) => {
          if (selectedFilters.indexOf(category.wordpress_id) !== -1) {
            response = true
          }
        })
      }

      return response
    })

  useEffect(() => {
    if (
      typeof localStorage !== 'undefined' &&
      defaultSelectedFilters === false
    ) {
      const storageSelectedFilters = localStorage.getItem(
        `${settings.id}-filters`
      )
      // const storageOffset: any = localStorage.getItem(`${settings.id}-offset`)

      if (storageSelectedFilters) {
        setSelectedFilters(JSON.parse(storageSelectedFilters))
      }

      // if (storageOffset) {
      //   setOffset(storageOffset)
      // }
    }
  }, [])

  const posts = filterPosts(allPosts, {
    ids: settings.postIds,
  })

  return (
    <BlogProvider
      value={{
        setSelectedFilters: setSelectedFiltersLocal,
        selectedFilters,
        isSelected,
        setOffset: setOffsetLocal,
        incrementOffset,
        decrementOffset,
        offset,
        limit: settings.limit,
        filterPosts,
        unfilteredPosts: allPosts,
        posts,
        categories: catEdges,
        hasPosts: posts.length > 0,
        showMoreButton:
          allPosts.length > posts.length &&
          offset * (settings.limit || 0) < allPosts.length,
        showLessButton: offset > 1,
      }}
    >
      {children}
    </BlogProvider>
  )
}

const Button = styled.button`
  outline: none;
  padding: 0;

  &:focus {
    outline: none;
  }
`

export const BlogFilter = ({ className = '', children, id }) => (
  <BlogConsumer>
    {(context) => (
      <Button
        className={className}
        role="button"
        data-active={context.isSelected(id) ? 1 : 0}
        onClick={() => {
          context.setSelectedFilters(id)
        }}
        tabIndex={0}
      >
        {children}
      </Button>
    )}
  </BlogConsumer>
)

export const BlogButton = ({ className, children, increment = true }) => (
  <BlogConsumer>
    {(context) => (
      <Button
        className={className}
        type="button"
        onClick={() => {
          if (increment) {
            context.incrementOffset()
          } else {
            context.decrementOffset()
          }
        }}
        tabIndex={0}
      >
        {children}
      </Button>
    )}
  </BlogConsumer>
)

export const BlogPagination = ({ className = '' }) => (
  <BlogConsumer>
    {(context) => {
      const paginations = context.unfilteredPosts.length / context.limit
      const pages = []

      for (let i = 0; i < paginations; i += 1) {
        pages.push(i + 1)
      }

      return (
        <div className={className}>
          {pages.map((page) => (
            <Button
              key={page}
              role="button"
              data-active={Number(page) === Number(context.offset) ? 1 : 0}
              onClick={() => {
                context.setOffset(page)
              }}
              tabIndex={0}
            >
              {page}
            </Button>
          ))}
        </div>
      )
    }}
  </BlogConsumer>
)

export default Blogik
